import { daysBetween } from "../../lib/funnel/helpers";
import type { UserRowV2 } from "./sql";

export type StageKey =
  | "calendar"
  | "first_bot"
  | "first_transcript"
  | "first_synopsis"
  | "second_synopsis"
  | "first_chat"
  | "custom_template"
  | "paid";

export type Stage = {
  key: StageKey;
  label: string;
  short: string;
  dateOf: (u: UserRowV2) => string | null;
};

export const STAGES: Stage[] = [
  { key: "calendar",         label: "Connected calendar",  short: "Calendar",        dateOf: (u) => u.connected_calendar_at },
  { key: "first_bot",        label: "First bot meeting",   short: "Bot meeting",     dateOf: (u) => u.first_bot_meeting_at },
  { key: "first_transcript", label: "First transcript",    short: "Transcript",      dateOf: (u) => u.first_transcript_at },
  { key: "first_synopsis",   label: "First summary",       short: "1st summary",     dateOf: (u) => u.first_synopsis_at },
  { key: "second_synopsis",  label: "Second summary",      short: "2nd summary",     dateOf: (u) => u.second_synopsis_at },
  { key: "first_chat",       label: "Asked Marloo",        short: "Ask Marloo",      dateOf: (u) => u.first_meeting_chat_at },
  { key: "custom_template",  label: "Created custom template", short: "Custom template", dateOf: (u) => u.first_custom_template_at },
  { key: "paid",             label: "Converted to paid",   short: "Paid",            dateOf: (u) => u.converted_to_paid_at },
];

// Days from signup to milestone, floored. Returns null if milestone never hit.
// Negative results (timestamp wobble) are clamped to 0.
export function daysSinceSignup(u: UserRowV2, milestoneIso: string | null): number | null {
  if (!milestoneIso) return null;
  const d = daysBetween(u.created_at_utc, milestoneIso);
  if (!Number.isFinite(d)) return null;
  return d < 0 ? 0 : d;
}

export function minutesSinceSignup(u: UserRowV2, milestoneIso: string | null): number | null {
  if (!milestoneIso) return null;
  const a = new Date(u.created_at_utc).getTime();
  const b = new Date(milestoneIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const m = Math.floor((b - a) / 60000);
  return m < 0 ? 0 : m;
}

export type Granularity = "15min" | "hour" | "day" | "week";

// Each finer granularity gets capped at the largest window where bar count
// per tile stays well under Recharts' ~1–2k/series limit. Beyond the cap,
// the page silently falls back to a coarser bucket size.
export const HOURLY_MAX_DAYS = 7;
export const FIFTEEN_MIN_MAX_DAYS = 1;

export type HistogramBucket = {
  // Minute-since-signup boundaries. All four granularities share this
  // canonical unit so the tooltip can render an exact range. 15min = 15,
  // hour = 60, day = 1440, week = 10080.
  minuteStart: number;
  minuteEnd: number;
  label: string;
  count: number;
};

export type StageHistogram = {
  stage: Stage;
  buckets: HistogramBucket[];
  cohortTotal: number;
  reachedCount: number;
};

// Minutes per bucket for each granularity. Drives both bar count and the
// minute-range label rendered in the tooltip.
function bucketMinutes(g: Granularity): number {
  switch (g) {
    case "15min": return 15;
    case "hour":  return 60;
    case "day":   return 60 * 24;
    case "week":  return 60 * 24 * 7;
  }
}

function bucketLabel(g: Granularity, idx: number): string {
  switch (g) {
    case "15min": {
      const day = Math.floor(idx / 96);
      const qHourOfDay = idx % 96;
      const hr = Math.floor(qHourOfDay / 4);
      const mn = (qHourOfDay % 4) * 15;
      return `d${day}·${String(hr).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
    }
    case "hour": return `h${idx}`;
    case "day":  return `d${idx}`;
    case "week": return `wk ${idx}`;
  }
}

// Build histograms for every stage off the full cohort. Buckets count users
// by when they first hit each stage, capped at observationDays. Recent
// sign-ups with less than the full window are still included; they simply
// have fewer days of opportunity.
export function buildHistograms(
  users: UserRowV2[],
  granularity: Granularity,
  observationDays: number,
  selectedKeys?: Set<StageKey>,
): StageHistogram[] {
  const cohortTotal = users.length;
  const observationMinutes = observationDays * 24 * 60;
  const minsPer = bucketMinutes(granularity);
  const bucketCount = Math.ceil(observationMinutes / minsPer);
  const stages = selectedKeys ? STAGES.filter((s) => selectedKeys.has(s.key)) : STAGES;
  return stages.map((stage) => {
    const counts = new Array(bucketCount).fill(0) as number[];
    let reached = 0;
    for (const u of users) {
      const ms = stage.dateOf(u);
      const m = minutesSinceSignup(u, ms);
      if (m == null || m >= observationMinutes) continue;
      reached += 1;
      const idx = Math.floor(m / minsPer);
      if (idx >= 0 && idx < bucketCount) {
        counts[idx] = (counts[idx] ?? 0) + 1;
      }
    }
    const buckets: HistogramBucket[] = [];
    for (let i = 0; i < bucketCount; i++) {
      buckets.push({
        minuteStart: i * minsPer,
        minuteEnd: Math.min(observationMinutes, (i + 1) * minsPer) - 1,
        label: bucketLabel(granularity, i),
        count: counts[i] ?? 0,
      });
    }
    return { stage, buckets, cohortTotal, reachedCount: reached };
  });
}

// Median days between two stages, computed over users who reached BOTH.
// Returns null if no users reached both.
export function medianGapDays(
  users: UserRowV2[],
  fromDateOf: (u: UserRowV2) => string | null,
  toDateOf: (u: UserRowV2) => string | null,
): number | null {
  const gaps: number[] = [];
  for (const u of users) {
    const a = fromDateOf(u);
    const b = toDateOf(u);
    if (!a || !b) continue;
    const d = daysBetween(a, b);
    if (!Number.isFinite(d)) continue;
    gaps.push(Math.max(0, d));
  }
  if (gaps.length === 0) return null;
  gaps.sort((x, y) => x - y);
  const mid = Math.floor(gaps.length / 2);
  return gaps.length % 2 === 0
    ? Math.round(((gaps[mid - 1] ?? 0) + (gaps[mid] ?? 0)) / 2)
    : (gaps[mid] ?? 0);
}

export function medianDaysToStage(users: UserRowV2[], stage: Stage): number | null {
  return medianGapDays(users, (u) => u.created_at_utc, stage.dateOf);
}

// "Deepest stage reached" before paying. Returns key of the latest STAGES
// entry (excluding paid) whose milestone exists and predates conversion.
export function deepestStageBeforePaid(u: UserRowV2): StageKey | "none" {
  if (!u.converted_to_paid_at) return "none";
  let deepest: StageKey | "none" = "none";
  for (const s of STAGES) {
    if (s.key === "paid") continue;
    const ms = s.dateOf(u);
    if (ms && ms <= u.converted_to_paid_at) deepest = s.key;
  }
  return deepest;
}
